// packages/web-ui/src/components/inline-cards/InlineCardErrorBoundary.tsx
//
// Defensive boundary around a single inline card. The SPA has no top-level
// error boundary, so a render-time throw inside ANY inline card would unmount
// the entire React tree (a blank "Tessa broke completely" screen). This
// boundary isolates the blast radius to the one failing card: it catches the
// throw, renders a small inline notice in place of that card, and leaves the
// rest of chat — and the app — fully interactive.
//
// React error boundaries must be class components (there is no hook equivalent
// for `componentDidCatch`), so this is the one class component in the inline-
// cards family.

import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface InlineCardErrorBoundaryProps {
  children: ReactNode;
  /** Short label for the failing card kind (surfaced in the fallback copy). */
  cardKind?: string;
}

interface InlineCardErrorBoundaryState {
  hasError: boolean;
}

export class InlineCardErrorBoundary extends Component<
  InlineCardErrorBoundaryProps,
  InlineCardErrorBoundaryState
> {
  constructor(props: InlineCardErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): InlineCardErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the isolated card failure for debugging without taking down the
    // whole SPA. (`no-console` is not an enabled rule in this package.)
    console.error(
      `InlineCardErrorBoundary: '${this.props.cardKind ?? 'inline-card'}' card failed to render`,
      error,
      info.componentStack,
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="status"
          data-testid="inline-card-error-boundary"
          data-card-kind={this.props.cardKind}
          className="border-taupe-300 bg-cream-50 text-taupe-700 rounded-none border p-2 text-xs"
        >
          This card couldn’t be displayed. The rest of the conversation is
          unaffected.
        </div>
      );
    }
    return this.props.children;
  }
}
